import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import LanguageIcon from '@mui/icons-material/Language';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../api/axiosInstance';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/StatCard';
import { useOrganizationSelect } from '../../context/OrganizationSelectContext';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function daysUntil(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
}

function expirationColor(days) {
  if (days === null) return 'default';
  if (days < 0) return 'error';
  if (days <= 30) return 'warning';
  return 'success';
}

function getLegacyOrganizationName(domain) {
  return domain.organizationDetails?.name || domain.Organization?.name || '';
}

function SummaryCards({ domains }) {
  const stats = useMemo(() => {
    const expired = domains.filter((domain) => {
      const days = daysUntil(domain.domainExpiration);
      return days !== null && days < 0;
    }).length;
    const soon = domains.filter((domain) => {
      const days = daysUntil(domain.domainExpiration);
      return days !== null && days >= 0 && days <= 30;
    }).length;
    return { total: domains.length, expired, soon };
  }, [domains]);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Box sx={{ flex: 1 }}>
        <StatCard icon={<Avatar sx={{ bgcolor: 'black' }}><LanguageIcon /></Avatar>} title="Domaines" value={stats.total} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <StatCard icon={<Avatar sx={{ bgcolor: '#d32f2f' }}><AccessTimeFilledIcon /></Avatar>} title="Expirés" value={stats.expired} bgColor={stats.expired ? '#ffebee' : undefined} borderColor={stats.expired ? '1px solid #d32f2f' : undefined} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <StatCard icon={<Avatar sx={{ bgcolor: '#fbc02d' }}><AccessTimeFilledIcon /></Avatar>} title="Expirations proches" value={stats.soon} interval="30 prochains jours" bgColor={stats.soon ? '#fffde7' : undefined} borderColor={stats.soon ? '1px solid #fbc02d' : undefined} />
      </Box>
    </Stack>
  );
}

function LegacyDomainsTab() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const { selectedOrganization } = useOrganizationSelect();

  const loadDomains = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/domain');
      let rows = (Array.isArray(data) ? data : []).filter((item) => !item.name?.toLowerCase().includes('onmicrosoft'));
      if (selectedOrganization) {
        rows = rows.filter((item) => getLegacyOrganizationName(item) === selectedOrganization);
      }
      setDomains(rows.sort((a, b) => new Date(a.domainExpiration || 0) - new Date(b.domainExpiration || 0)));
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger les domaines.' });
    } finally {
      setLoading(false);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const columns = useMemo(() => [
    { accessorKey: 'name', header: 'Nom de domaine', size: 260, filterVariant: 'autocomplete' },
    {
      accessorKey: 'domainExpiration',
      header: "Date d'expiration",
      size: 150,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      id: 'daysRemaining',
      header: 'Jours restants',
      size: 120,
      accessorFn: (row) => daysUntil(row.domainExpiration),
      Cell: ({ cell }) => {
        const days = cell.getValue();
        return <Chip size="small" color={expirationColor(days)} label={days === null ? '-' : `${days} j`} />;
      },
    },
    {
      id: 'organization',
      header: 'Client',
      accessorFn: getLegacyOrganizationName,
      filterVariant: 'autocomplete',
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: domains,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: { density: 'compact', showColumnFilters: true, showGlobalFilter: true, pagination: { pageSize: 50 } },
    state: { isLoading: loading },
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
  });

  return (
    <Stack spacing={2.5}>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
      <SummaryCards domains={domains} />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Liste des noms de domaines</Typography>
          <MaterialReactTable table={table} />
        </Stack>
      </Paper>
    </Stack>
  );
}

function OvhDomainsTab() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/domain/ovh');
      setDomains(data.domains || []);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger les domaines OVH.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const syncDomains = async () => {
    setSyncing(true);
    setFeedback(null);
    try {
      const { data } = await axios.post('/api/domain/ovh/sync');
      setFeedback({
        type: data.unmapped ? 'warning' : 'success',
        message: `Synchronisation terminée : ${data.domains || 0} domaine(s), ${data.unmapped || 0} non mappé(s), ${data.failed || 0} erreur(s).`,
      });
      await loadDomains();
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Synchronisation OVH impossible.' });
    } finally {
      setSyncing(false);
    }
  };

  const columns = useMemo(() => [
    { accessorKey: 'name', header: 'Domaine', size: 220, filterVariant: 'autocomplete' },
    { accessorKey: 'ovhAccountName', header: 'Compte OVH', size: 160, filterVariant: 'autocomplete' },
    {
      accessorKey: 'domainExpiration',
      header: 'Expiration',
      size: 130,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      id: 'daysRemaining',
      header: 'Jours',
      size: 90,
      accessorFn: (row) => daysUntil(row.domainExpiration),
      Cell: ({ cell }) => {
        const days = cell.getValue();
        return <Chip size="small" color={expirationColor(days)} label={days === null ? '-' : `${days} j`} />;
      },
    },
    { accessorKey: 'ovhRenewalType', header: 'Renouvellement', size: 150, filterVariant: 'autocomplete' },
    {
      id: 'matchedClient',
      header: 'Client matché',
      size: 240,
      accessorFn: (row) => row.mappedOrganization?.name || row.matchedOrganizationName || '',
      Cell: ({ row }) => {
        const name = row.original.mappedOrganization?.name || row.original.matchedOrganizationName || '';
        return name ? <Chip size="small" color="success" variant="outlined" label={name} /> : <Chip size="small" color="warning" label="À mapper" />;
      },
    },
    {
      id: 'serviceInfoStatus',
      header: 'Infos OVH',
      size: 160,
      accessorFn: (row) => row.rawData?.syncError || '',
      Cell: ({ row }) => row.original.rawData?.syncError
        ? <Chip size="small" color="error" label="serviceInfos refusé" />
        : <Chip size="small" color="success" variant="outlined" label="OK" />,
    },
    { accessorKey: 'lastSyncAt', header: 'Dernière sync', size: 150, Cell: ({ cell }) => formatDate(cell.getValue()) },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: domains,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: { density: 'compact', showColumnFilters: true, showGlobalFilter: true, pagination: { pageSize: 50 } },
    state: { isLoading: loading || syncing },
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
  });

  const unmapped = domains.filter((domain) => !domain.mappedOrganization?._id && !domain.matchedOrganizationName).length;

  return (
    <Stack spacing={2.5}>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
      <SummaryCards domains={domains} />
      {unmapped ? <Alert severity="warning">{unmapped} domaine(s) OVH sans client. Le mapping se règle dans Paramètres &gt; OVH.</Alert> : null}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>Domaines OVH</Typography>
            <Button startIcon={<SyncRoundedIcon />} variant="outlined" onClick={syncDomains} disabled={syncing}>Synchroniser</Button>
          </Stack>
          <MaterialReactTable table={table} />
        </Stack>
      </Paper>
    </Stack>
  );
}

export default function Domain() {
  const [tab, setTab] = useState('general');

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <PageHeader section="Infrastructure" title="Noms de domaines" />
        <Paper variant="outlined" sx={{ px: 2, borderRadius: 2 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="general" label="Général" />
            <Tab value="ovh" label="OVH" />
          </Tabs>
        </Paper>
        {tab === 'general' ? <LegacyDomainsTab /> : <OvhDomainsTab />}
      </Stack>
    </Box>
  );
}
